import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

import { AppSettingsService } from '~/core/services/appsettings.service';
import { TabNavigationService } from '~/core/services/tab-navigation.service';
import { AppSettings } from '~/core/models/appsettings';

import { routes } from '~/classes/routes/app.routing';

export class NavItem {
    public title: string = "";
    public icon: string = "";
    public imageIcon?: string = "";
    public Id?: string = "";
    public Count?: number = 0;
    public url: string = "";
    public Selected?: boolean = false;
    public roles?: string[] = [];
    public visible?: boolean = false;
}

@Component({
    selector: 'nav',
    templateUrl: './nav.component.html',
    styleUrls: ['./nav.component.scss'],
    standalone: false
})

export class NavComponent {
    @Input() expand: boolean = false;
    @Output() setLayoutClass = new EventEmitter();
    @Output() reloadPage = new EventEmitter();
    @Output() showSearch: EventEmitter<boolean> = new EventEmitter();
    @Output() uploadModalRequested = new EventEmitter<void>();

    public appIcon: string = "web_asset";
    public appTitle: string = "Application Mode";
    public selectedBU: string = "";

    public menuIcon: string = "keyboard_double_arrow_right";
    public activeItem: NavItem = new NavItem();
    public isBuSelected: boolean = false;
    public isHomeActive: boolean = false;
    public isUploadActive: boolean = false;

    public navItems: any = [];
    public navSubItems: any = [];

    private _routes = [...routes];
    private collapse: boolean = false;
    private search: boolean = false;
    private isInWellView: boolean = false;

    constructor(
        public router: Router,
        private appSettingsService: AppSettingsService,
        private tabNavService: TabNavigationService
    ) { }

    //*************************************************************************
    //  Component Life-Cycle Methods
    //*************************************************************************
    public async ngOnInit() {
        this.selectedBU = sessionStorage.getItem('selectedBU') || 'ABU';
        this.checkBuSelection();

        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe(() => {
            this.checkBuSelection();
        });

        await this.getRoutes();

        if (this.router?.url) {
            let routeUrl = this.router.url.split("?")[0];

            this.setActiveItem(this.navItems, routeUrl);
            this.setActiveItem(this.navSubItems, routeUrl);
        }
    }

    //*************************************************************************
    //  Public Methods
    //*************************************************************************
    public toggleMenu(collapse: boolean = false) {
        this.collapse = collapse;
        this.menuIcon = collapse ? "keyboard_double_arrow_left" : "keyboard_double_arrow_right";

        this.setLayoutClass.emit();
    }

    public navigateTo(item: NavItem) {
        if (!this.isBuSelected) return;

        if (this.collapse) {
            this.collapse = false;
            this.setLayoutClass.emit();
        }

        this.router.navigateByUrl(item.url);
        this.activeItem = item;
    }

    public toggleFullscreen() {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen();
            this.appIcon = "iframe";
            this.appTitle = "Web Mode";
        } else {
            document.exitFullscreen();
            this.appIcon = "web_asset";
            this.appTitle = "Application Mode";
        }
    }

    public onNavItemMouseMove(event: MouseEvent, element: HTMLElement): void {
        if (this.expand) return; // Only show tooltip when navbar is collapsed
        
        const rect = element.getBoundingClientRect();
        const tooltip = element.querySelector('.title') as HTMLElement;
        
        if (tooltip) {
            // Position tooltip near cursor
            const tooltipX = event.clientX + 10; // 10px offset from cursor
            const tooltipY = event.clientY + 10;
            
            tooltip.style.setProperty('--tooltip-x', `${tooltipX}px`);
            tooltip.style.setProperty('--tooltip-y', `${tooltipY}px`);
        }
    }

    public navigateToHome() {
        // Home is always accessible
        if (this.collapse) {
            this.collapse = false;
            this.setLayoutClass.emit();
        }

        sessionStorage.removeItem('selectedBU');
        this.isBuSelected = false;
        this.isUploadActive = false;
        this.activeItem = new NavItem();
        this.checkBuSelection();
        
        // Signal that home navigation is happening (to reset backlog page and state)
        // This happens BEFORE route navigation
        console.log('[NavComponent] Triggering home navigation reset signal');
        this.tabNavService.triggerHomeNavigation();
        
        // Only navigate if not already on home
        if (this.router.url !== '/') {
            console.log('[NavComponent] Navigating to home from:', this.router.url);
            this.router.navigate(['/']);
        } else {
            console.log('[NavComponent] Already on home, just reset state');
        }
    }

    public openUploadModal() {
        if (!this.isBuSelected) return;
        this.uploadModalRequested.emit();
    }

    //*************************************************************************
    //  Private Methods
    //*************************************************************************
    private async getRoutes() {
        this.navItems = [];
        this.navSubItems = [];

        await this.appSettingsService.getSettings().then((appSettings: AppSettings) => {
            this._routes.forEach((route: any) => {
                if (route.isNav) {
                    let addItem: boolean = false;

                    if (route.roles) {
                        if (route.roles.some((r: any) => appSettings.roles.includes(r))) {
                            addItem = true;
                        }
                    } else {
                        addItem = true;
                    }

                    if (addItem) {
                        this.navItems.push(
                            {
                                "title": route.title,
                                "icon": route.icon,
                                "imageIcon": route.imageIcon,
                                "url": route.path
                            }
                        )

                        this.getSubRoutes(route, appSettings.roles);
                    }
                }
            })
        });
    }

    private getSubRoutes(parent: any, roles: any) {
        this._routes.forEach((route: any) => {
            if (route.isSubNav && route.path.startsWith(`${parent.path}/`)) {
                let addItem: boolean = false;

                if (route.roles) {
                    if (route.roles.some((r: any) => roles.includes(r))) {
                        addItem = true;
                    }
                } else {
                    addItem = true;
                }

                if (addItem) {
                    this.navSubItems.push(
                        {
                            "parent": parent.title,
                            "title": route.title,
                            "icon": route.icon,
                            "url": route.path
                        }
                    )
                }
            }
        });
    }

    private setActiveItem(items: any, routeUrl: any) {
        for (let item of items) {
            if (`/${item.url}` === routeUrl) {
                this.activeItem = item;
                break;
            }
        }
    }

    private checkBuSelection() {
        // Check if we are on the root path (BU selection page) or generated backlog page
        const currentUrl = this.router.url.split('?')[0];
        this.isHomeActive = currentUrl === '/' || currentUrl === '/generated-backlog';
        this.isBuSelected = !!sessionStorage.getItem('selectedBU');
    }
}
